import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { type VariantProps } from 'class-variance-authority'
import { cn } from '../../helpers/classnames'
import { josefButtonVariants } from './button-variants'

export interface JosefButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof josefButtonVariants> {
  /**
   * Content to display inside the button
   */
  children: ReactNode
  /**
   * Whether the button is in a loading state
   */
  isLoading?: boolean
  /**
   * Custom loading text to show when isLoading is true
   */
  loadingText?: string
  /**
   * Icon to display on the left side of the button
   */
  leftIcon?: ReactNode
  /**
   * Icon to display on the right side of the button
   */
  rightIcon?: ReactNode
  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * Josef's custom button component with black/white color palette
 *
 * Features:
 * - Clean black/white design that inverts in dark mode
 * - All standard variants: primary, secondary, outline, ghost, destructive, link
 * - Multiple sizes: sm, md, lg, xl, icon variants
 * - Loading states with spinner
 * - Left/right icon support
 * - Full width option
 * - Focus ring and accessibility features
 *
 * @example
 * ```tsx
 * <JosefButton variant="primary" size="lg">
 *   Click me
 * </JosefButton>
 *
 * <JosefButton variant="outline" leftIcon={<PlusIcon />}>
 *   Add item
 * </JosefButton>
 *
 * <JosefButton variant="ghost" isLoading loadingText="Saving...">
 *   Save
 * </JosefButton>
 * ```
 */
export const JosefButton = forwardRef<HTMLButtonElement, JosefButtonProps>(
  (
    {
      children,
      variant,
      size,
      fullWidth,
      isLoading = false,
      loadingText,
      leftIcon,
      rightIcon,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled || isLoading}
        className={cn(
          josefButtonVariants({ variant, size, fullWidth }),
          className
        )}
        {...props}
      >
        {/* Loading Spinner */}
        {isLoading && (
          <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
        )}

        {/* Left Icon */}
        {!isLoading && leftIcon && (
          <span className="flex-shrink-0">{leftIcon}</span>
        )}

        {/* Button Text */}
        <span className={cn(isLoading && loadingText ? 'block' : 'block')}>
          {isLoading && loadingText ? loadingText : children}
        </span>

        {/* Right Icon */}
        {!isLoading && rightIcon && (
          <span className="flex-shrink-0">{rightIcon}</span>
        )}
      </button>
    )
  }
)

JosefButton.displayName = 'JosefButton'

export default JosefButton